// Role-key derivation for the vault metadata/content key split.
//
// Byte-identical to the Rust `wiser_e2e::envelope::derive_subkey` used by
// `wiser-vault/agent`'s `derive_role_key`: HKDF-SHA256(ikm = CEK,
// salt = "wiser-vault/keysplit/v1", info = role). The contract is pinned in
// ../vectors/keysplit-vectors.json (asserted by both languages).
//
// See wiser-vault/docs/SPEC-vault-key-split-activation.md §4.

/** The independently-sealable roles of a vault CEK. String union (not a
 *  `const enum`, which has cross-package transpile hazards). */
export const KeyRole = { Metadata: 'metadata', Content: 'content' } as const;
export type KeyRole = (typeof KeyRole)[keyof typeof KeyRole];

/** The canonical HKDF `info` bytes per role — the single source of truth, so a
 *  stray string literal elsewhere can never drift the derivation. */
const ROLE_INFO = Object.freeze({
  metadata: new TextEncoder().encode('metadata'),
  content: new TextEncoder().encode('content'),
}) satisfies Record<KeyRole, Uint8Array>;

/** Reserved HKDF salt for vault role-key derivation (distinct from every other
 *  envelope/KDF domain in the tree). */
const KEYSPLIT_SALT = new TextEncoder().encode('wiser-vault/keysplit/v1');

/**
 * Derive a role's 32-byte sub-key from a vault CEK via HKDF-SHA256 (RFC 5869).
 * Deterministic; distinct roles are computationally separated under HKDF's PRF
 * security. Must reproduce ../vectors/keysplit-vectors.json byte-for-byte.
 */
export async function deriveRoleKey(cek: Uint8Array, role: KeyRole): Promise<Uint8Array> {
  if (cek.length !== 32) throw new Error('deriveRoleKey: CEK must be 32 bytes');
  const info = ROLE_INFO[role];
  if (!info) throw new Error(`deriveRoleKey: unknown role: ${String(role)}`);
  // Match the module's importKey idiom: pass a fresh ArrayBuffer view (avoids the
  // Uint8Array→BufferSource / SharedArrayBuffer strictness under TS 5.x lib.dom).
  const ikm = cek.buffer.slice(cek.byteOffset, cek.byteOffset + cek.byteLength) as ArrayBuffer;
  const base = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: KEYSPLIT_SALT as BufferSource, info: info as BufferSource },
    base,
    256,
  );
  const out = new Uint8Array(bits);
  if (out.length !== 32) throw new Error('deriveRoleKey: derived key must be 32 bytes');
  return out;
}
